import * as React from "react";
import {
  ColorPreference,
  OfficeThemeReading,
  PaneMode,
  resolvePaneModeWithPreference,
} from "../platform/shared/officeTheme";

// Keeps the pane's colour scheme in step with the Office theme.
//
// Office exposes its theme at `Office.context.officeTheme`, but not everywhere: it is
// absent on some platforms and its `isDarkTheme` flag is unsupported in Outlook. Every
// read is therefore guarded and the decision itself lives in a pure module
// (platform/shared/officeTheme) where it can be tested. Under Jest there is no Office
// global at all, which is why nothing here touches it at module scope -- the same rule
// platform/index.tsx follows for host detection, and for the same reason.

/** Read the host's theme, or null when the host does not expose one. Never throws. */
export const readOfficeTheme = (): OfficeThemeReading | null => {
  try {
    if (typeof Office === "undefined") return null;
    const officeTheme = Office.context?.officeTheme;
    if (!officeTheme) return null;
    return {
      isDarkTheme: officeTheme.isDarkTheme,
      bodyBackgroundColor: officeTheme.bodyBackgroundColor,
    };
  } catch {
    return null;
  }
};

/**
 * Reports the scheme through `onMode` once on mount, then again on the two changes we
 * can hear about. ColorPreferenceProvider owns the result and applies it.
 *
 * Outlook raises `OfficeThemeChanged` (Mailbox 1.14) when the user switches theme with
 * the pane open. The document hosts have no equivalent event, so a Word user who goes
 * dark mid-session sees the change on their next pane open. The operating system's
 * preference is only consulted when Office gave us nothing to go on, and only then does
 * a change in it move the pane.
 *
 * An explicit `preference` from the header menu beats all of it. The listeners still
 * run -- they resolve through the preference, so they are no-ops while it is set -- which
 * keeps "system" exactly as it was and lets switching back to it pick up Office's theme
 * on the spot rather than on the next pane open.
 */
const OfficeThemeSync = ({
  preference,
  onMode: setMode,
}: {
  preference: ColorPreference;
  onMode: (mode: PaneMode) => void;
}) => {
  React.useEffect(() => {
    const media =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null;

    const apply = (reading: OfficeThemeReading | null) =>
      setMode(resolvePaneModeWithPreference(preference, reading, media?.matches ?? false));

    apply(readOfficeTheme());

    const onMediaChange = () => {
      if (readOfficeTheme() === null) apply(null);
    };
    media?.addEventListener("change", onMediaChange);

    // Only Outlook has the event, and only via the mailbox. The document hosts' type
    // for `Office.context` declares `mailbox` as always present; at runtime it is
    // undefined outside Outlook, hence the cast.
    let mailbox: Office.Mailbox | undefined;
    try {
      mailbox =
        typeof Office !== "undefined"
          ? (Office.context?.mailbox as Office.Mailbox | undefined)
          : undefined;
    } catch {
      mailbox = undefined;
    }
    const onOfficeThemeChanged = (args: Office.OfficeThemeChangedEventArgs) =>
      apply(args.officeTheme);
    const canListen = typeof mailbox?.addHandlerAsync === "function";
    if (mailbox && canListen) {
      try {
        mailbox.addHandlerAsync(Office.EventType.OfficeThemeChanged, onOfficeThemeChanged);
      } catch {
        // Older Outlook: the event is not in the requirement set. The mount-time read
        // still applied, which is the most such a host can offer.
      }
    }

    return () => {
      media?.removeEventListener("change", onMediaChange);
      if (mailbox && canListen && typeof mailbox.removeHandlerAsync === "function") {
        try {
          mailbox.removeHandlerAsync(Office.EventType.OfficeThemeChanged);
        } catch {
          // Nothing to undo.
        }
      }
    };
  }, [setMode, preference]);

  return null;
};

export default OfficeThemeSync;
